import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import TicketsManagerTabs from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";

import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { CircularProgress } from "@material-ui/core";
import { getBackendUrl } from "../../config";
import logo from "../../assets/logo.png";
import logoDark from "../../assets/logo-black.png";

const minTicketsManagerWidth = 404;
const maxTicketsManagerWidth = 700;
const defaultTicketsManagerWidth = minTicketsManagerWidth;
const ticketsWidthStorageKey = "ticketsManagerWidth";

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		padding: "2px",
		height: `calc(100% - 48px)`,
		overflowY: "hidden",
	},
	chatPapper: {
		display: "flex",
		height: "100%",
		overflowX: "hidden",
	},
	contactsWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflowY: "hidden",
		overflowX: "hidden",
		position: "relative",
		paddingRight: "4px",
		boxSizing: "border-box",
		// Adicionar largura mínima como fallback
		minWidth: `${minTicketsManagerWidth}px`,
	},
	messagesWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		flexGrow: 1,
	},
	welcomeMsg: {
		background: "transparent",
		border: "none",
		boxShadow: "none",
		display: "flex",
		justifyContent: "space-evenly",
		alignItems: "center",
		height: "100%",
		textAlign: "center",
	},
	dragger: {
		width: "12px",
		cursor: "col-resize",
		position: "absolute",
		top: 0,
		right: 0,
		bottom: 0,
		zIndex: 100,
		backgroundColor: "transparent",
		userSelect: "none",
		touchAction: "none",
		"&::before": {
			content: '""',
			position: "absolute",
			top: 0,
			bottom: 0,
			right: 0,
			width: "1px",
			backgroundColor: "#ddd",
		},
		"&:hover::before": {
			backgroundColor: "#bdbdbd",
		},
	},
	logo: {
		logo: theme.logo,
		content: "url(" + (theme.mode === "light" 
			? theme.calculatedLogoLight() 
			: theme.calculatedLogoDark()) + ")"
	},
}));

const TicketsCustom = () => {
	const { user } = useContext(AuthContext);
	
	// ⚠️ CORREÇÃO PRINCIPAL: Inicializar com largura padrão adequada
	const getInitialWidth = () => {
		const persistedWidth = Number(localStorage.getItem(ticketsWidthStorageKey));
		const userWidth = Number(user?.defaultTicketsManagerWidth);
		const candidateWidth = Number.isFinite(persistedWidth) && persistedWidth > 0
			? persistedWidth
			: (Number.isFinite(userWidth) && userWidth > 0 ? userWidth : defaultTicketsManagerWidth);
		return Math.max(minTicketsManagerWidth, Math.min(maxTicketsManagerWidth, candidateWidth));
	};
	const [ticketsManagerWidth, setTicketsManagerWidth] = useState(getInitialWidth);
	
	const classes = useStyles({ ticketsManagerWidth });
	const { ticketId } = useParams();
	const ticketsManagerWidthRef = useRef(ticketsManagerWidth);
	const lastPersistedWidthRef = useRef(ticketsManagerWidth);

	// ⚠️ CORREÇÃO: useEffect mais robusto para inicialização
	useEffect(() => {
		const persistedWidth = Number(localStorage.getItem(ticketsWidthStorageKey));
		const userWidth = Number(user?.defaultTicketsManagerWidth);
		const initialWidth = Number.isFinite(persistedWidth) && persistedWidth > 0
			? persistedWidth
			: (Number.isFinite(userWidth) && userWidth > 0 ? userWidth : defaultTicketsManagerWidth);
		
		// Garantir que a largura esteja dentro dos limites
		const validWidth = Math.max(
			minTicketsManagerWidth,
			Math.min(maxTicketsManagerWidth, initialWidth)
		);
		
		setTicketsManagerWidth(validWidth);
		ticketsManagerWidthRef.current = validWidth;
		lastPersistedWidthRef.current = validWidth;
	}, [user]);

	const handleMouseDown = (e) => {
		e.preventDefault();
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
		document.addEventListener("mouseup", handleMouseUp, true);
		document.addEventListener("mousemove", handleMouseMove, true);
	};

	const handleSaveContact = async (value) => {
		// Garantir largura mínima antes de salvar
		const validValue = Math.max(minTicketsManagerWidth, Math.min(maxTicketsManagerWidth, value));
		localStorage.setItem(ticketsWidthStorageKey, String(validValue));
		
		try {
			await api.put(`/users/toggleChangeWidht/${user.id}`, { 
				defaultTicketsManagerWidth: validValue 
			});
		} catch (error) {
			console.error("Erro ao salvar largura:", error);
		}
	};

	const handleMouseMove = useCallback((e) => {
		const newWidth = e.clientX - document.body.offsetLeft;
		
		if (newWidth >= minTicketsManagerWidth && newWidth <= maxTicketsManagerWidth) {
			ticketsManagerWidthRef.current = newWidth;
			setTicketsManagerWidth(newWidth);
		}
	}, []);

	const handleMouseUp = async () => {
		document.removeEventListener("mouseup", handleMouseUp, true);
		document.removeEventListener("mousemove", handleMouseMove, true);
		document.body.style.cursor = "";
		document.body.style.userSelect = "";

		const newWidth = ticketsManagerWidthRef.current;

		if (newWidth !== lastPersistedWidthRef.current) {
			await handleSaveContact(newWidth);
			lastPersistedWidthRef.current = newWidth;
		}
	};

	// ⚠️ CORREÇÃO: Garantir que a largura nunca seja 0 ou inválida
	const effectiveWidth = Math.max(minTicketsManagerWidth, ticketsManagerWidth);

	return (
		<QueueSelectedProvider>
			<div className={clsx(classes.chatContainer, "neumorphic-flat")}>
				<div className={clsx(classes.chatPapper, "neumorphic-flat")}>
					<div
						className={classes.contactsWrapper}
						style={{ 
							width: `${effectiveWidth}px`,
							// Adicionar fallbacks importantes
							minWidth: `${minTicketsManagerWidth}px`,
							maxWidth: `${maxTicketsManagerWidth}px`,
							// Garantir visibilidade
							opacity: effectiveWidth > 0 ? 1 : 0,
							visibility: effectiveWidth > 0 ? 'visible' : 'hidden'
						}}
					>
						<TicketsManagerTabs />
						<div 
							onMouseDown={handleMouseDown} 
							className={classes.dragger} 
						/>
					</div>
					<div className={classes.messagesWrapper}>
						{ticketId ? (
							<Ticket />
						) : (
							<Hidden only={["sm", "xs"]}>
								<Paper square className={classes.welcomeMsg} elevation={0}>
									<span>
										<center>
											<img className={classes.logo} width="50%" alt="" />
										</center>
										{i18n.t("chat.noTicketMessage")}
									</span>
								</Paper>
							</Hidden>
						)}
					</div>
				</div>
			</div>
		</QueueSelectedProvider>
	);
};

export default TicketsCustom;
